import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as ProcessingIcon,
  Assignment as LogIcon
} from '@mui/icons-material';
import { getImportStatus, getImportLog } from '../services/api/datasetService';
import ImportLogDialog from './ImportLogDialog';

/**
 * DatasetDetailsDialog - Shows dataset import history and details
 *
 * Features:
 * - Display dataset metadata
 * - Show import history (all imports for this dataset)
 * - Show import details (status, progress, errors)
 * - Auto-refresh when import is in progress
 */
export default function DatasetDetailsDialog({ open, dataset, onClose }) {
  const [importStatus, setImportStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [viewingLog, setViewingLog] = useState(null);

  useEffect(() => {
    if (open && dataset) {
      loadImportStatus();
    }
  }, [open, dataset]);

  // Auto-refresh when import is in progress
  useEffect(() => {
    if (!autoRefresh || !open) return;

    const interval = setInterval(() => {
      loadImportStatus();
    }, 3000); // Refresh every 3 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, open]);

  async function loadImportStatus() {
    if (!dataset) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getImportStatus(dataset.dataset_id);
      setImportStatus(data);

      // Enable auto-refresh if any import is in progress
      const hasActiveImport = data.imports?.some(
        imp => imp.status === 'PENDING' || imp.status === 'PROCESSING'
      );
      setAutoRefresh(hasActiveImport);
    } catch (err) {
      console.error('Failed to load import status:', err);
      setError(err.message);
      setAutoRefresh(false);
    } finally {
      setLoading(false);
    }
  }

  function getStatusIcon(status) {
    switch (status) {
      case 'COMPLETED':
        return <SuccessIcon color="success" fontSize="small" />;
      case 'COMPLETED_WITH_ERRORS':
        return <ErrorIcon color="warning" fontSize="small" />;
      case 'FAILED':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'PROCESSING':
        return <ProcessingIcon color="info" fontSize="small" />;
      case 'PENDING':
        return <PendingIcon color="warning" fontSize="small" />;
      default:
        return null;
    }
  }

  function getStatusColor(status) {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'COMPLETED_WITH_ERRORS':
        return 'warning';
      case 'FAILED':
        return 'error';
      case 'PROCESSING':
        return 'info';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  }

  function formatDate(isoString) {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function formatDuration(startTime, endTime) {
    if (!startTime) return 'N/A';
    if (!endTime) return 'In progress...';

    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    const durationSec = Math.round(durationMs / 1000);

    if (durationSec < 60) return `${durationSec}s`;
    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;
    return `${minutes}m ${seconds}s`;
  }

  if (!dataset) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{dataset.dataset_name}</Typography>
          <Chip
            label={dataset.status}
            color={dataset.status === 'ACTIVE' ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Dataset Info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Dataset Information
          </Typography>
          <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Dataset ID:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{dataset.dataset_id}</Typography>

            <Typography variant="body2" color="text.secondary">Description:</Typography>
            <Typography variant="body2">{dataset.description || 'No description'}</Typography>

            <Typography variant="body2" color="text.secondary">Person Count:</Typography>
            <Typography variant="body2">{dataset.person_count || 0}</Typography>

            <Typography variant="body2" color="text.secondary">Created:</Typography>
            <Typography variant="body2">{formatDate(dataset.created_at)}</Typography>

            <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
            <Typography variant="body2">{formatDate(dataset.updated_at)}</Typography>
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* Import History */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="overline" color="text.secondary">
            Import History
          </Typography>
          {autoRefresh && (
            <Chip
              icon={<CircularProgress size={16} />}
              label="Auto-refreshing"
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !importStatus && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && importStatus && importStatus.imports?.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No imports yet
            </Typography>
          </Box>
        )}

        {importStatus && importStatus.imports?.length > 0 && (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {[...importStatus.imports].reverse().map((imp, index) => (
              <ListItem
                key={imp.import_id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  bgcolor: index === 0 ? 'action.hover' : 'background.paper'
                }}
              >
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {getStatusIcon(imp.status)}
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                    Import #{importStatus.imports.length - index}
                  </Typography>
                  <Chip
                    label={imp.status}
                    color={getStatusColor(imp.status)}
                    size="small"
                  />
                </Box>

                <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: '120px 1fr', gap: 0.5, fontSize: '0.875rem' }}>
                  <Typography variant="caption" color="text.secondary">Import ID:</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{imp.import_id}</Typography>

                  <Typography variant="caption" color="text.secondary">Filename:</Typography>
                  <Typography variant="caption">{imp.filename || 'N/A'}</Typography>

                  <Typography variant="caption" color="text.secondary">Started:</Typography>
                  <Typography variant="caption">{formatDate(imp.started_at)}</Typography>

                  {imp.completed_at && (
                    <>
                      <Typography variant="caption" color="text.secondary">Completed:</Typography>
                      <Typography variant="caption">{formatDate(imp.completed_at)}</Typography>

                      <Typography variant="caption" color="text.secondary">Duration:</Typography>
                      <Typography variant="caption">{formatDuration(imp.started_at, imp.completed_at)}</Typography>
                    </>
                  )}

                  {imp.status === 'PROCESSING' && (
                    <>
                      <Typography variant="caption" color="text.secondary">Progress:</Typography>
                      <Typography variant="caption">
                        {imp.persons_processed || 0} / {imp.total_persons || '?'} persons
                      </Typography>
                    </>
                  )}

                  {imp.status === 'COMPLETED' && (
                    <>
                      <Typography variant="caption" color="text.secondary">Persons Imported:</Typography>
                      <Typography variant="caption">{imp.persons_processed || 0}</Typography>
                    </>
                  )}

                  {imp.status === 'FAILED' && imp.error_message && (
                    <>
                      <Typography variant="caption" color="error">Error:</Typography>
                      <Typography variant="caption" color="error">{imp.error_message}</Typography>
                    </>
                  )}
                </Box>

                {/* View Log Button */}
                {(imp.status === 'COMPLETED' || imp.status === 'COMPLETED_WITH_ERRORS' || imp.status === 'FAILED') && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={<LogIcon />}
                      onClick={() => setViewingLog({ datasetId: dataset.dataset_id, importId: imp.import_id })}
                      sx={{ textTransform: 'none' }}
                    >
                      View Import Log
                    </Button>
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Import Log Dialog */}
      {viewingLog && (
        <ImportLogDialog
          open={Boolean(viewingLog)}
          datasetId={viewingLog.datasetId}
          importId={viewingLog.importId}
          onClose={() => setViewingLog(null)}
        />
      )}
    </Dialog>
  );
}
